/**
 * Scatter History chart filter options and application logic.
 * TWA filters for file/aggregate modes; grade threshold + tack entry for maneuver_stats.
 */
import type { ScatterHistoryDataMode } from '../services/scatterHistoryDataService';
import { filterByTwa } from './dataFiltering';
import { normalizeManeuverGradeFilterThreshold } from './maneuverGradeFilter';

/**
 * Live scatter/probability: keep only rows whose time is within the active playback window [startMs, endMs].
 * Used by the live copies of the History pages (which pin the date to the live session and show the active
 * window instead of a user-chosen date range). Reads `Datetime` (Date or ISO), falling back to `ts` seconds/ms.
 */
export function filterRowsToLiveActiveWindow<T extends Record<string, unknown>>(
  rows: T[],
  startMs: number,
  endMs: number
): T[] {
  if (!rows?.length || !Number.isFinite(startMs) || !Number.isFinite(endMs)) {return rows;}
  return rows.filter((r) => {
    const d = (r as Record<string, unknown>).Datetime ?? (r as Record<string, unknown>).datetime;
    let t = NaN;
    if (d != null) {t = d instanceof Date ? d.getTime() : new Date(d as string).getTime();}
    if (!Number.isFinite(t)) {
      const ts = (r as Record<string, unknown>).ts;
      if (typeof ts === 'number') {t = ts < 1e12 ? ts * 1000 : ts;}
    }
    return Number.isFinite(t) && t >= startMs && t <= endMs;
  });
}

export type ScatterHistoryTwaFilterId = 'upwind' | 'downwind' | 'reaching' | 'port' | 'stbd';

export type ScatterHistoryManeuverFilterId =
  | 'grade_all'
  | 'grade_gt_1'
  | 'grade_gt_2'
  | 'port_entry'
  | 'stbd_entry';

export type ScatterHistoryChartFilterId = ScatterHistoryTwaFilterId | ScatterHistoryManeuverFilterId;

export interface ScatterHistoryChartFilterOption {
  id: ScatterHistoryChartFilterId;
  label: string;
  /** When true, only one filter in the same group may be active (grade or entry side). */
  radioGroup?: 'grade' | 'entry';
}

export const SCATTER_HISTORY_TWA_FILTER_IDS: ScatterHistoryTwaFilterId[] = [
  'upwind',
  'downwind',
  'reaching',
  'port',
  'stbd',
];

export const SCATTER_HISTORY_MANEUVER_FILTER_IDS: ScatterHistoryManeuverFilterId[] = [
  'grade_all',
  'grade_gt_1',
  'grade_gt_2',
  'port_entry',
  'stbd_entry',
];

/**
 * start_stats rows carry GRADE (from the dataset_events join) but have no maneuver
 * entry side / TWA channel, so they expose grade filters only.
 */
export const SCATTER_HISTORY_START_STATS_FILTER_IDS: ScatterHistoryManeuverFilterId[] = [
  'grade_all',
  'grade_gt_1',
  'grade_gt_2',
];

/** Relational, per-event modes (maneuver_stats / start_stats) share grade-based filtering. */
export function isScatterHistoryRelationalEventMode(dataMode: ScatterHistoryDataMode): boolean {
  return dataMode === 'maneuver_stats' || dataMode === 'start_stats';
}

const TWA_FILTER_OPTIONS: ScatterHistoryChartFilterOption[] = SCATTER_HISTORY_TWA_FILTER_IDS.map((id) => ({
  id,
  label: id,
}));

const MANEUVER_FILTER_OPTIONS: ScatterHistoryChartFilterOption[] = [
  { id: 'grade_all', label: 'Grade All', radioGroup: 'grade' },
  { id: 'grade_gt_1', label: '> 1', radioGroup: 'grade' },
  { id: 'grade_gt_2', label: '> 2', radioGroup: 'grade' },
  { id: 'port_entry', label: 'Port entry', radioGroup: 'entry' },
  { id: 'stbd_entry', label: 'Stbd entry', radioGroup: 'entry' },
];

const START_STATS_FILTER_OPTIONS: ScatterHistoryChartFilterOption[] = [
  { id: 'grade_all', label: 'Grade All', radioGroup: 'grade' },
  { id: 'grade_gt_1', label: '> 1', radioGroup: 'grade' },
  { id: 'grade_gt_2', label: '> 2', radioGroup: 'grade' },
];

export function getScatterHistoryChartFilterOptions(
  dataMode: ScatterHistoryDataMode
): ScatterHistoryChartFilterOption[] {
  if (dataMode === 'maneuver_stats') {return MANEUVER_FILTER_OPTIONS;}
  if (dataMode === 'start_stats') {return START_STATS_FILTER_OPTIONS;}
  return TWA_FILTER_OPTIONS;
}

export function isScatterHistoryTwaFilterId(id: string): id is ScatterHistoryTwaFilterId {
  return (SCATTER_HISTORY_TWA_FILTER_IDS as string[]).includes(id);
}

export function isScatterHistoryManeuverFilterId(id: string): id is ScatterHistoryManeuverFilterId {
  return (SCATTER_HISTORY_MANEUVER_FILTER_IDS as string[]).includes(id);
}

export function stripIncompatibleScatterHistoryFilters(
  filters: string[] | undefined,
  dataMode: ScatterHistoryDataMode
): string[] {
  const list = filters ?? [];
  if (dataMode === 'maneuver_stats') {
    return list.filter((f) => isScatterHistoryManeuverFilterId(f));
  }
  if (dataMode === 'start_stats') {
    return list.filter((f) => (SCATTER_HISTORY_START_STATS_FILTER_IDS as string[]).includes(f));
  }
  return list.filter((f) => isScatterHistoryTwaFilterId(f));
}

function rowGrade(row: Record<string, unknown>): number | null {
  const raw = row.grade ?? row.Grade ?? row.GRADE;
  if (raw == null) {return null;}
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
}

function rowTwaEntry(row: Record<string, unknown>): number | null {
  const raw = row.twa_entry ?? row.Twa_entry;
  if (raw == null) {return null;}
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
}

/** Grade threshold from maneuver filter token or global grade selection (-1 = All). */
export function maneuverGradeMinThresholdFromFilters(filters: string[]): number | null {
  if (filters.includes('grade_gt_2')) {return 2;}
  if (filters.includes('grade_gt_1')) {return 1;}
  if (filters.includes('grade_all')) {return -1;}
  return null;
}

export function maneuverGradeMinThresholdFromGlobalGrades(grades: (string | number)[]): number | null {
  if (!grades.length) {return null;}
  const nums = grades
    .map((g) => normalizeManeuverGradeFilterThreshold(typeof g === 'string' ? Number(g) : g))
    .filter((n) => !Number.isNaN(n));
  if (!nums.length) {return null;}
  return Math.min(...nums);
}

function applyManeuverGradeThreshold(data: Record<string, unknown>[], minThreshold: number | null): Record<string, unknown>[] {
  if (minThreshold == null || minThreshold < 0) {return data;}
  return data.filter((row) => {
    const grade = rowGrade(row);
    if (grade == null) {return false;}
    return grade > minThreshold;
  });
}

function applyManeuverEntrySide(
  data: Record<string, unknown>[],
  entry: 'PORT' | 'STBD' | null
): Record<string, unknown>[] {
  if (entry == null) {return data;}
  return data.filter((row) => {
    const twa = rowTwaEntry(row);
    if (twa == null) {return false;}
    return entry === 'PORT' ? twa < 0 : twa > 0;
  });
}

function maneuverEntryFromChartFilters(filters: string[]): 'PORT' | 'STBD' | null {
  if (filters.includes('port_entry')) {return 'PORT';}
  if (filters.includes('stbd_entry')) {return 'STBD';}
  return null;
}

function applyManeuverChartFilters(data: Record<string, unknown>[], filters: string[]): Record<string, unknown>[] {
  if (!filters.length) {return data;}
  let out = data;
  const gradeThreshold = maneuverGradeMinThresholdFromFilters(filters);
  if (gradeThreshold != null && gradeThreshold >= 0) {
    out = applyManeuverGradeThreshold(out, gradeThreshold);
  }
  out = applyManeuverEntrySide(out, maneuverEntryFromChartFilters(filters));
  return out;
}

/** Apply per-chart filters saved in scatter history builder JSON. */
export function applyScatterHistoryChartFilters(
  data: Record<string, unknown>[] | null | undefined,
  filters: string[] | null | undefined
): Record<string, unknown>[] {
  if (!data?.length) {return data ?? [];}
  const list = filters ?? [];
  if (!list.length) {return data;}

  const twaFilters = list.filter(isScatterHistoryTwaFilterId);
  const maneuverFilters = list.filter(isScatterHistoryManeuverFilterId);

  if (maneuverFilters.length > 0) {
    return applyManeuverChartFilters(data, list);
  }
  if (twaFilters.length > 0) {
    return filterByTwa(data, twaFilters, [], [], []) as Record<string, unknown>[];
  }
  return data;
}

export interface ScatterExploreGlobalFilterState {
  states?: (string | number)[];
  races?: (string | number)[];
  legs?: (string | number)[];
  grades?: (string | number)[];
}

/**
 * Explore scatter filter pipeline: chart.filters baseline, then global PageSettings filters.
 * Render-time selection/cut/lasso are applied separately in SimpleScatter.
 */
export function applyScatterExploreFilters(
  data: Record<string, unknown>[] | null | undefined,
  chartFilters: string[] | null | undefined,
  globals: ScatterExploreGlobalFilterState
): Record<string, unknown>[] {
  if (!data?.length) {return data ?? [];}

  let out = data;
  const states = globals.states ?? [];
  const races = globals.races ?? [];
  const legs = globals.legs ?? [];
  const grades = globals.grades ?? [];

  if (chartFilters?.length) {
    out = applyScatterHistoryChartFilters(out, chartFilters);
  }
  out = filterByTwa(out, states, races, legs, grades) as Record<string, unknown>[];

  return out;
}

export interface ScatterHistoryGlobalManeuverFilterState {
  grades?: (string | number)[];
  tackEntry?: 'PORT' | 'STBD' | null;
  races?: (string | number)[];
  legs?: (string | number)[];
}

/** Global maneuver_stats filters from PageSettings / filterStore (no per-chart filters). */
export function applyScatterHistoryGlobalManeuverFilters(
  data: Record<string, unknown>[] | null | undefined,
  state: ScatterHistoryGlobalManeuverFilterState
): Record<string, unknown>[] {
  if (!data?.length) {return data ?? [];}

  let out = [...data];

  const gradeThreshold = maneuverGradeMinThresholdFromGlobalGrades(state.grades ?? []);
  if (gradeThreshold != null && gradeThreshold >= 0) {
    out = applyManeuverGradeThreshold(out, gradeThreshold);
  }

  out = applyManeuverEntrySide(out, state.tackEntry ?? null);

  const races = (state.races ?? []).map(String);
  const legs = (state.legs ?? []).map(String);

  if (races.length > 0 || legs.length > 0) {
    out = filterByTwa(out, [], races, legs, []) as Record<string, unknown>[];
  }

  return out;
}

/** Client-side filter stack for explore history pages (respects maneuver_stats grade thresholds). */
export function applyScatterHistoryClientFilters(
  data: Record<string, unknown>[] | null | undefined,
  options: {
    chartFilters?: string[] | null;
    dataMode?: ScatterHistoryDataMode;
    globals: ScatterExploreGlobalFilterState;
    maneuverTackEntry?: 'PORT' | 'STBD' | null;
  }
): Record<string, unknown>[] {
  if (!data?.length) {return data ?? [];}

  const chartFilters = options.chartFilters ?? [];
  const globals = options.globals;

  if (isScatterHistoryRelationalEventMode(options.dataMode ?? 'server_file')) {
    let out = applyScatterHistoryGlobalManeuverFilters(data, {
      // start_stats has no maneuver entry side; passing null makes the entry filter a no-op.
      grades: globals.grades,
      tackEntry: options.dataMode === 'maneuver_stats' ? (options.maneuverTackEntry ?? null) : null,
      races: globals.races,
      legs: globals.legs,
    });
    if (chartFilters.length > 0) {
      out = applyScatterHistoryChartFilters(out, chartFilters);
    }
    return out;
  }

  return applyScatterExploreFilters(data, chartFilters, globals);
}

/** Toggle a chart filter with radio-group behavior for grade/entry pills. */
export function toggleScatterHistoryChartFilter(
  currentFilters: string[],
  filterId: ScatterHistoryChartFilterId,
  dataMode: ScatterHistoryDataMode
): string[] {
  const options = getScatterHistoryChartFilterOptions(dataMode);
  const option = options.find((o) => o.id === filterId);
  if (!option) {return currentFilters;}

  const exists = currentFilters.includes(filterId);
  if (exists) {
    return currentFilters.filter((f) => f !== filterId);
  }

  let next = [...currentFilters, filterId];
  if (option.radioGroup === 'grade') {
    next = next.filter(
      (f) => !options.some((o) => o.radioGroup === 'grade' && o.id !== filterId && o.id === f)
    );
  } else if (option.radioGroup === 'entry') {
    next = next.filter(
      (f) => !options.some((o) => o.radioGroup === 'entry' && o.id !== filterId && o.id === f)
    );
  }
  return next;
}

/**
 * Metadata filters applied client-side to server_file scatter/probability rows.
 * SQL-backed modes apply these server-side via the `filters` JSON, so this is only used
 * for the raw-parquet (server_file) path.
 * - year: exact match on the dataset's `year_name`
 * - event: case-insensitive contains-match on the dataset's `event_name`
 * - config: case-insensitive contains-match on the per-sample `Config_code` channel
 * - foilingStates: H0/H1/H2 match on the per-sample `Foiling_state` (int 0/1/2) channel
 */
export interface ScatterHistoryMetadataFilterState {
  year?: string[];
  event?: string[];
  config?: string[];
  foilingStates?: string[];
}

function rowYearName(row: Record<string, unknown>): string | null {
  const raw = row.year_name ?? row.Year_name ?? row.YEAR_NAME;
  if (raw == null) {return null;}
  const s = String(raw).trim();
  return s.length > 0 ? s : null;
}

function rowEventName(row: Record<string, unknown>): string | null {
  const raw = row.event_name ?? row.Event_name ?? row.EVENT_NAME ?? row.event;
  if (raw == null) {return null;}
  const s = String(raw).trim();
  return s.length > 0 ? s : null;
}

function rowConfigCode(row: Record<string, unknown>): string | null {
  const raw = row.Config_code ?? row.config_code ?? row.CONFIG_CODE;
  if (raw == null) {return null;}
  const s = String(raw).trim();
  return s.length > 0 ? s : null;
}

/** Map a raw foiling-state value to canonical H0/H1/H2. File rows carry the int 0/1/2. */
function rowFoilingStateLabel(row: Record<string, unknown>): string | null {
  const raw =
    row.Foiling_state ?? row.foiling_state ?? row.FOILING_STATE ?? row.State ?? row.state ?? row.STATE;
  if (raw == null || raw === '') {return null;}
  const s = String(raw).trim();
  // Already an H-label (SQL rows expose tags->>'FOILING_STATE' as "State").
  if (/^h\d$/i.test(s)) {return s.toUpperCase();}
  const n = Number(s);
  if (!Number.isFinite(n)) {return null;}
  // 0->H0, 1->H1, 2->H2; anything else falls back to H1 (matches the processing default=1).
  if (n === 0) {return 'H0';}
  if (n === 2) {return 'H2';}
  return 'H1';
}

/** True when any metadata filter dimension is active. */
export function hasScatterHistoryMetadataFilters(
  filters: ScatterHistoryMetadataFilterState | null | undefined
): boolean {
  if (!filters) {return false;}
  return (
    (filters.year?.length ?? 0) > 0 ||
    (filters.event?.length ?? 0) > 0 ||
    (filters.config?.length ?? 0) > 0 ||
    (filters.foilingStates?.length ?? 0) > 0
  );
}

/** Apply Year/Event/Config/foiling-State metadata filters to server_file rows. */
export function applyScatterHistoryMetadataFilters(
  data: Record<string, unknown>[] | null | undefined,
  filters: ScatterHistoryMetadataFilterState | null | undefined
): Record<string, unknown>[] {
  if (!data?.length) {return data ?? [];}
  if (!hasScatterHistoryMetadataFilters(filters)) {return data;}

  const years = (filters!.year ?? []).map((y) => String(y).trim().toLowerCase()).filter(Boolean);
  const events = (filters!.event ?? []).map((e) => String(e).trim().toLowerCase()).filter(Boolean);
  const configs = (filters!.config ?? []).map((c) => String(c).trim().toLowerCase()).filter(Boolean);
  const states = new Set(
    (filters!.foilingStates ?? []).map((s) => String(s).trim().toUpperCase()).filter(Boolean)
  );

  return data.filter((row) => {
    if (years.length > 0) {
      const y = rowYearName(row);
      if (y == null || !years.includes(y.toLowerCase())) {return false;}
    }
    if (events.length > 0) {
      const ev = rowEventName(row);
      if (ev == null) {return false;}
      const evLower = ev.toLowerCase();
      if (!events.some((token) => evLower.includes(token))) {return false;}
    }
    if (configs.length > 0) {
      const cfg = rowConfigCode(row);
      if (cfg == null) {return false;}
      const cfgLower = cfg.toLowerCase();
      if (!configs.some((token) => cfgLower.includes(token))) {return false;}
    }
    if (states.size > 0) {
      const label = rowFoilingStateLabel(row);
      if (label == null || !states.has(label)) {return false;}
    }
    return true;
  });
}

/** After sidebar Cut: filter scatter rows by event_id or datetime ranges. */
export function applyScatterHistorySelectionCutFilter(
  data: Record<string, unknown>[] | null | undefined,
  cutEventsList: unknown[]
): Record<string, unknown>[] {
  if (!data?.length || !cutEventsList.length) {return data ?? [];}

  const first = cutEventsList[0];
  if (typeof first === 'number') {
    const ids = new Set(
      cutEventsList.map((e) => Number(e)).filter((n) => Number.isFinite(n))
    );
    return data.filter((d) => {
      const raw = d.event_id ?? d.event_ID ?? d.EVENT_ID;
      if (raw == null) {return false;}
      const eid = Number(raw);
      return Number.isFinite(eid) && ids.has(eid);
    });
  }

  const hasTimeRanges = cutEventsList.some(
    (range) =>
      range != null &&
      typeof range === 'object' &&
      'start_time' in (range as object) &&
      'end_time' in (range as object)
  );
  if (!hasTimeRanges) {return data ?? [];}

  return data.filter((d) => {
    const rawTs = d.Datetime ?? d.datetime ?? d.timestamp;
    if (rawTs == null) {return false;}
    const ts =
      rawTs instanceof Date ? rawTs.getTime() : new Date(String(rawTs)).getTime();
    if (!Number.isFinite(ts)) {return false;}
    return cutEventsList.some((range) => {
      if (typeof range !== 'object' || range == null) {return false;}
      const r = range as { start_time?: string; end_time?: string };
      if (!r.start_time || !r.end_time) {return false;}
      return (
        ts >= new Date(r.start_time).getTime() &&
        ts <= new Date(r.end_time).getTime()
      );
    });
  });
}
